// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getPageLinkByNodeId, useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getButtonIconField, { ButtonIcon, buttonIconOptions } from "@store/Framework/Common/Fields/Toolkit/buttonIcon";
import getLinkTypeField, { LinkType, linkTypeOptions } from "@store/Framework/Common/Fields/Toolkit/linkType";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import getCurrentSegmentFromUrl from "@toolkit/Framework/Common/Utilities/getCurrentSegmentFromUrl";
import React, { FC } from "react";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import { css } from "styled-components";
import { focusStyles } from "@toolkit/Themes/common/mixins";
import getColor from "@insite/mobius/utilities/getColor";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { transitionHoverStyles } from "@store/Themes/common/mixins/transition";
import breakpoint from "@toolkit/Framework/Common/Utilities/breakpoint";

const enum fields {
    linkDestination = "linkDestination",
    linkFileUrl = "linkFileUrl",
    linkIcon = "linkIcon",
    linkIconPosition = "linkIconPosition",
    linkOpenInNewWindow = "linkOpenInNewWindow",
    linkOverriddenTitle = "linkOverriddenTitle",
    linkType = "linkType",
    linkUniqueId = "linkUniqueId",
    links = "links",
}

const buttonIconField = getButtonIconField(fields.linkIcon);
const linkTypeField = getLinkTypeField(fields.linkType);

export interface LinkTilesLinkProps {
    fields: {
        [fields.linkDestination]: LinkFieldValue;
        [fields.linkFileUrl]: string;
        [fields.linkIcon]: ButtonIcon;
        [fields.linkIconPosition]: "right" | "left";
        [fields.linkOpenInNewWindow]: boolean;
        [fields.linkOverriddenTitle]: string;
        [fields.linkUniqueId]: string;
        [fields.linkType]: LinkType;
    };
}

export interface LinkTilesProps extends WidgetProps {
    fields: {
        [fields.links]: LinkTilesLinkProps[];
    };
}

export interface LinkTilesStyles {
    wrapper?: InjectableCss;
    link?: ClickablePresentationProps;
    linkIcon?: IconPresentationProps;
}

export const linkTilesStyles: LinkTilesStyles = {
    wrapper: {
        css: css`
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(${pxToRem(230)}, 1fr));
            gap: ${pxToRem(8)};

            ${breakpoint(1100)} {
                grid-template-columns: repeat(auto-fill, minmax(${pxToRem(240)}, 1fr));
            }

            ${breakpoint(1300)} {
                grid-template-columns: repeat(auto-fill, minmax(${pxToRem(285)}, 1fr));
            }
        `,
    },
    link: {
        css: css`
            ${focusStyles()}
            display: flex;
            align-items: center;
            gap: ${pxToRem(4)};
            min-height: ${pxToRem(68)};
            padding: ${pxToRem(8, 24)};
            background-color: ${getColor("common.background")};
            border: 1px solid ${getColor("common.border")};
            border-radius: ${pxToRem(6)};
            font-weight: ${x => x.theme.design.typography.weight.bold};
            line-height: ${x => x.theme.design.typography.lineHeight.semi};
            transition: ${transitionHoverStyles("background-color", "border-color", "color")};

            &:hover,
            &:active {
                background-color: ${getColor("primary.lightest")};
                border-color: ${getColor("primary")};
                color: ${getColor("primary")};
            }
        `,
    },
    linkIcon: {
        color: "primary",
        size: 24,
    },
};

const styles = linkTilesStyles;

const LinkTiles: FC<LinkTilesProps> = ({ fields }) => {
    const { links, hasMissingCategories } = useGetLinks(fields.links, x => x.fields.linkDestination);

    if (hasMissingCategories) {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            {fields.links.map(({ fields: linkFields }, index) => {
                const linkIconSrc = buttonIconField.parse(linkFields.linkIcon);
                const linkType = linkTypeField.parse(linkFields.linkType);
                const isFile = linkType === linkTypeOptions.file.id;
                const linkTitle =
                    linkFields.linkOverriddenTitle ||
                    (isFile ? getCurrentSegmentFromUrl(linkFields.linkFileUrl) : links[index]?.title) ||
                    "";
                const linkUrl = isFile ? linkFields.linkFileUrl : links[index]?.url || "";

                if (!linkUrl) {
                    return null;
                }

                const linkIcon = linkIconSrc ? <Icon {...styles.linkIcon} src={linkIconSrc} /> : null;

                return (
                    <Clickable
                        {...styles.link}
                        href={linkUrl}
                        key={linkFields.linkUniqueId}
                        target={linkFields.linkOpenInNewWindow ? "_blank" : "_self"}
                    >
                        {linkFields.linkIconPosition === "left" && linkIcon}
                        {linkTitle}
                        {linkFields.linkIconPosition === "right" && linkIcon}
                    </Clickable>
                );
            })}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: LinkTiles,
    definition: {
        group: "Basic",
        displayName: "Link Tiles",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const {
                        linkDestination: { type, value },
                        linkOverriddenTitle,
                    } = item.fields;
                    if (linkOverriddenTitle) {
                        return linkOverriddenTitle;
                    }
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }

                        return displayValue;
                    }

                    return value;
                },
                defaultValue: [],
                fieldType: "Contextual",
                fieldDefinitions: [
                    {
                        name: fields.linkUniqueId,
                        editorTemplate: "UniqueIdField",
                    },
                    {
                        ...linkTypeField.definition,
                    },
                    {
                        name: fields.linkFileUrl,
                        displayName: "File",
                        editorTemplate: "FilePickerField",
                        defaultValue: "",
                        isVisible: x => x.fields.linkType === linkTypeOptions.file.id,
                    },
                    {
                        name: fields.linkDestination,
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: fields.linkOverriddenTitle,
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: fields.linkOpenInNewWindow,
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        ...buttonIconField.definition,
                        displayName: "Icon",
                    },
                    {
                        name: fields.linkIconPosition,
                        displayName: "Icon Position",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Right", value: "right" },
                            { displayName: "Left", value: "left" },
                        ],
                        defaultValue: "left",
                        hideEmptyOption: true,
                        isVisible: x => x.fields.linkIcon !== buttonIconOptions.none.id,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
